import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring, useReducedMotion, MotionConfig } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { useTranslation } from "react-i18next";
import { scrollToSection } from "@/lib/scroll";

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });

  // Scroll parallax (disabled under reduced-motion by collapsing the ranges).
  const artY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 110]);
  const artScale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 90]);

  // Subtle desktop mouse parallax (progressive enhancement only).
  const mx = useSpring(0, { stiffness: 55, damping: 18 });
  const my = useSpring(0, { stiffness: 55, damping: 18 });
  const onMove = (e) => {
    if (reduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 26);
    my.set(((e.clientY - r.top) / r.height - 0.5) * 16);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  const lines = [t("hero.titleLine1"), t("hero.titleLine2"), t("hero.titleLine3Prefix")];

  return (
    <MotionConfig reducedMotion="user">
      <section
        ref={ref}
        id="top"
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-20"
      >
        {/* ===== Immersive cinematic art layer (decorative background, not content) ===== */}
        <motion.div style={{ y: artY, scale: artScale }} className="pointer-events-none absolute inset-0">
          <motion.div style={{ x: mx, y: my }} className="w-full h-full">
            <img
              src="/hero/arca-hero.webp"
              srcSet="/hero/arca-hero-mobile.webp 960w, /hero/arca-hero.webp 1672w"
              sizes="100vw"
              alt=""
              aria-hidden="true"
              loading="eager"
              decoding="async"
              className="arca-bg-drift w-full h-full object-cover object-[70%_32%] md:object-[78%_center] lg:object-[right_center] scale-[1.08]"
            />
          </motion.div>
        </motion.div>

        {/* Slow-pulsing intelligence glow anchored on the Arca emblem */}
        <div className="arca-orb-glow pointer-events-none absolute right-[6%] top-[20%] w-[24rem] h-[24rem] md:w-[30rem] md:h-[30rem] rounded-full bg-[#0aa2ff]/25 blur-[100px]" />
        {/* Autonomous orbital light drifting around the Arca emblem */}
        <div className="pointer-events-none absolute right-[6%] top-[20%] w-[24rem] h-[24rem] md:w-[30rem] md:h-[30rem]">
          <div className="arca-orbit absolute inset-0">
            <div className="absolute left-1/2 top-1 -translate-x-1/2 w-24 h-24 rounded-full bg-[#4fd0ff]/25 blur-[46px]" />
          </div>
        </div>
        {/* Subtle drifting atmosphere for autonomous depth */}
        <div className="arca-atmos pointer-events-none absolute right-[2%] top-[8%] w-[34rem] h-[34rem] rounded-full bg-[#0055FF]/10 blur-[130px]" />

        {/* Edge blends: left text-contrast scrim, bottom + top section blends, mobile darkening */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#050505] from-25% via-[#050505]/65 via-55% to-transparent to-90%" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#050505]/85 to-transparent" />
        <div className="pointer-events-none absolute inset-0 bg-[#050505]/45 md:bg-transparent" />
        <div className="pointer-events-none absolute left-0 top-1/3 w-[30rem] h-[30rem] bg-[#0055FF]/12 blur-[140px] rounded-full" />

        {/* ===== Live, fully-interactive hero content (single source of truth) ===== */}
        <motion.div style={{ y: textY }} className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[#00F0FF] border border-white/10 rounded-full px-4 py-1.5 mb-8 bg-white/5 backdrop-blur-sm"
            data-testid="hero-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] animate-pulse" />
            {t("hero.badge")}
          </motion.div>

          <h1 className="font-display font-extrabold text-white leading-[0.95] tracking-tighter text-5xl sm:text-6xl md:text-7xl lg:text-8xl max-w-5xl [text-shadow:0_2px_40px_rgba(0,0,0,0.55)]">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  custom={i}
                  variants={lineVariant}
                  initial="hidden"
                  animate="show"
                  className={`block ${i === 2 ? "text-glow" : ""}`}
                >
                  {i === 2 ? (
                    <>
                      {t("hero.titleLine3Prefix")} <span className="text-[#0055FF]">{t("hero.titleLine3Highlight")}</span>
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-8 text-lg md:text-xl text-slate-300 max-w-xl leading-relaxed [text-shadow:0_1px_24px_rgba(0,0,0,0.6)]"
          >
            {t("hero.subtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <button
              onClick={() => scrollToSection("#chat")}
              className="group inline-flex items-center justify-center gap-2 bg-[#0055FF] text-white px-8 py-4 rounded-2xl text-base font-semibold shadow-[0_0_40px_rgba(0,85,255,0.5)] hover:shadow-[0_0_60px_rgba(0,85,255,0.75)] hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300"
              data-testid="hero-try-demo"
            >
              {t("hero.tryBuilder")}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <button
              className="inline-flex items-center justify-center gap-2 border border-white/20 text-white px-8 py-4 rounded-2xl text-base font-semibold hover:bg-white/5 transition-colors duration-300 backdrop-blur-sm"
              data-testid="hero-watch-video"
            >
              <Play size={16} /> {t("hero.watchFilm")}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="mt-14 font-mono text-xs uppercase tracking-[0.2em] text-slate-500"
          >
            {t("hero.shippedWith")}
          </motion.div>
        </motion.div>
      </section>
    </MotionConfig>
  );
}
